import {clsx} from 'clsx'
import {pullRequestProcessingIndicatorPath} from '@github-ui/paths'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {ProfileReference} from '@github-ui/profile-reference'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {ReactNode} from 'react'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {Label, Link, RelativeTime} from '@primer/react'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type PullRequestState as PullRequestStateType, PullRequestState} from '../types'
import styles from './PullRequestHeaderSummary.module.css'
import {PullRequestsProcessingIndicatorWithDataFetching} from '@github-ui/pull-requests-processing-indicator/PullRequestsProcessingIndicator'
import {isEnterprise} from '@github-ui/runtime-environment'
import {DiffStat} from './DiffStat'
import {getAgentAppSlug, PullRequestHeaderAuthors} from './PullRequestHeaderAuthors'
import {PullRequestHeaderBranches, type PullRequestHeaderBranchesProps} from './PullRequestHeaderBranches'
import {useIsOverviewEnabled} from '../hooks/use-feature-state'
import {useLayoutQuery} from '../page-data/loaders/use-layout-query'

export interface PullRequestHeaderSummaryProps extends PullRequestHeaderBranchesProps {
  author: {
    login: string
    displayName?: string | null
    avatarUrl?: string | null
    isAgent?: boolean
    slug?: string | null
  }
  attributedUser?: {
    login: string
    displayName?: string | null
    avatarUrl?: string | null
  } | null
  className?: string
  commitsCount: number
  isInAdvisoryRepo?: boolean
  isEditing?: boolean
  leadingContent?: ReactNode
  mergedBy?: string | null
  mergedByAvatarUrl?: string | null
  mergedByName?: string | null
  mergedTime?: string
  pullRequestId: number
  pullRequestNumber: number
  repositoryId: number
  setIsEditing?: (isEditing: boolean) => void
  state: PullRequestStateType
}

export function PullRequestHeaderSummary({
  author,
  attributedUser,
  baseBranch,
  baseRepositoryDefaultBranch = '',
  baseRepositoryName = '',
  baseRepositoryOwnerLogin = '',
  canChangeBase = false,
  className,
  commitsCount,
  headBranch,
  headRepositoryOwnerLogin = '',
  headRepositoryName = '',
  isInAdvisoryRepo,
  isEditing = false,
  leadingContent,
  mergedBy,
  mergedByAvatarUrl,
  mergedByName,
  mergedTime,
  pullRequestId,
  pullRequestNumber,
  repositoryId,
  setIsEditing,
  state,
  stack,
}: PullRequestHeaderSummaryProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  const showUpdatedHeader = useIsOverviewEnabled()
  const {
    data: {
      pullRequest: {closedBy, closedByAvatarUrl, closedByName, closedTime, createdTime},
    },
  } = useLayoutQuery()
  const merger = mergedBy ? {login: mergedBy, displayName: mergedByName, avatarUrl: mergedByAvatarUrl} : null
  const closer = closedBy ? {login: closedBy, displayName: closedByName, avatarUrl: closedByAvatarUrl} : null
  const summaryActor = !showUpdatedHeader && state === PullRequestState.Merged ? merger : author
  const messageText = getSummaryMessageText(state, commitsCount, summaryActor?.login)
  const terminalActor = state === PullRequestState.Merged ? merger : state === PullRequestState.Closed ? closer : null
  const terminalVerb =
    state === PullRequestState.Merged ? 'merged' : state === PullRequestState.Closed ? 'closed' : null
  const terminalTime = state === PullRequestState.Merged ? mergedTime : closedTime
  const sameActor = terminalActor?.login.toLowerCase() === author.login.toLowerCase()

  // Show Copilot alongside the human it is attributed to when the PR author is an agent.
  // Only applies while the author (not a merger) is the summary actor.
  const authorIsAgent = author.isAgent ?? isCopilot(author.login)
  const showCoAttribution =
    isFeatureEnabled('pull_request_copilot_attribution_header') &&
    summaryActor === author &&
    authorIsAgent &&
    !!attributedUser

  function renderActor(actor: PullRequestHeaderSummaryProps['author']) {
    const isAgent = showUpdatedHeader && actor === author && authorIsAgent
    const agentSlug = isAgent ? getAgentAppSlug(actor) : null
    const hovercardAttributes = agentSlug
      ? getHovercardAttributesForType('copilot', {botSlug: agentSlug})
      : getHovercardAttributesForType('user', {login: actor.login})

    return (
      <Link
        muted
        inline={!showUpdatedHeader}
        className={clsx('text-bold', showUpdatedHeader && styles.actor)}
        {...hovercardAttributes}
        href={agentSlug ? `/apps/${agentSlug}` : `/${actor.login}`}
      >
        {showUpdatedHeader && actor.avatarUrl && <GitHubAvatar src={actor.avatarUrl} alt="" size={20} />}
        <ProfileReference login={actor.login} profileName={actor.displayName} isAgent={isAgent} />
      </Link>
    )
  }

  const actor =
    showCoAttribution && attributedUser ? (
      <PullRequestHeaderAuthors
        agent={author}
        attributedUser={attributedUser}
        showAvatars={showUpdatedHeader}
        className={showUpdatedHeader ? styles.actor : undefined}
      />
    ) : summaryActor ? (
      renderActor(summaryActor)
    ) : null

  const terminalAttribution = terminalVerb ? (
    <span>
      {sameActor ? (
        <span>{` and ${terminalVerb} `}</span>
      ) : terminalActor ? (
        <>
          <span> and </span>
          {renderActor(terminalActor)}
          <span>{` ${terminalVerb} `}</span>
        </>
      ) : (
        <span>{`; ${terminalVerb} `}</span>
      )}
      {terminalTime && <RelativeTime datetime={terminalTime} />}
    </span>
  ) : null

  const branches = (
    <PullRequestHeaderBranches
      baseBranch={baseBranch}
      baseRepositoryDefaultBranch={baseRepositoryDefaultBranch}
      baseRepositoryOwnerLogin={baseRepositoryOwnerLogin}
      baseRepositoryName={baseRepositoryName}
      canChangeBase={canChangeBase}
      headBranch={headBranch}
      headRepositoryOwnerLogin={headRepositoryOwnerLogin}
      headRepositoryName={headRepositoryName}
      isInAdvisoryRepo={isInAdvisoryRepo}
      isEditing={isEditing}
      setIsEditing={setIsEditing}
      stack={stack}
    />
  )

  const processingIndicator = state === PullRequestState.Open && !isEnterprise() && (
    <PullRequestsProcessingIndicatorWithDataFetching
      processingIndicatorUrl={pullRequestProcessingIndicatorPath({
        owner: baseRepositoryOwnerLogin,
        repo: baseRepositoryName,
        number: pullRequestNumber,
      })}
      repositoryId={repositoryId}
      pullRequestId={pullRequestId}
    />
  )

  return (
    <>
      <div
        className={clsx(
          'fgColor-muted d-flex flex-items-center',
          styles.summaryContainer,
          showUpdatedHeader ? styles.updatedSummary : 'overflow-hidden',
          className,
        )}
      >
        {showUpdatedHeader ? (
          <>
            <div className={styles.actorGroup}>
              {leadingContent}
              <span className={styles.attribution}>
                {actor}
                {authorIsAgent && (
                  <>
                    {' '}
                    <Label variant="secondary">AI</Label>
                  </>
                )}
                <span> opened {createdTime && <RelativeTime datetime={createdTime} />}</span>
                {terminalAttribution}
              </span>
            </div>
            <div className={styles.branchGroup}>
              {branches}
              <DiffStat className={styles.diffStat} />
              {processingIndicator}
            </div>
          </>
        ) : (
          <>
            {actor} {messageText}
            {branches}
            {state === PullRequestState.Merged && <RelativeTime datetime={mergedTime} />}
            {processingIndicator}
          </>
        )}
      </div>
    </>
  )
}

function getSummaryMessageText(state: PullRequestState, commitsCount: number, actor?: string) {
  const commitsNumberText = `${commitsCount} ${commitsCount > 1 ? 'commits' : 'commit'}`

  if (state === PullRequestState.Merged) {
    if (actor) {
      return `merged ${commitsNumberText} into`
    } else {
      return `${commitsNumberText} merged into`
    }
  } else {
    return `wants to merge ${commitsNumberText} into`
  }
}
